import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {RegistrationRestService} from '../../../api/services/registration.rest.service';
import {Router} from '@angular/router';
import {PasswordValidator} from '../../../core/validators/password-validator';
import {ResponseNotificationService} from "../../../core/services/response-notification.service";
import {environment} from "../../../../environments/environment";
import {ResponseErrorsHandlerService} from "../../../core/services/response-errors-handler.service";
import {LoaderService} from "../../../core/services/loader.service";
import {TranslateService} from '@ngx-translate/core';
import {StaticPageRestService} from '../../../api/services/static-page.rest.service';
import {StaticPageApiModel} from '../../../api/models/static-page.api.model';
import {Subscription} from 'rxjs';
import {AppConfigRestService} from "../../../api/services/app-config.rest.service";
import {AppConfigApiModel} from "../../../api/models/app-config.api-model";
import {appChannelCode} from '../../../core/feature-flags/app-channel';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit {

  public registerForm: UntypedFormGroup;
  public termsRoute = '/pages/terms';
  public privacyPolicyRoute = '/pages/privacy-policy';
  public showAccessCode = false;
  public appConfig: AppConfigApiModel | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private regService: RegistrationRestService,
    private router: Router,
    public rns: ResponseNotificationService,
    private reh: ResponseErrorsHandlerService,
    private ls: LoaderService,
    private readonly translate: TranslateService,
    private readonly staticPageRS: StaticPageRestService,
    private readonly appConfigRS: AppConfigRestService
  ) {
  }

  ngOnInit() {
      this.createForm();
      this.loadLegalRoutes();
      this.loadRegistrationSettings();
      this.subscriptions.push(this.translate.onLangChange.subscribe(() => {
        this.loadLegalRoutes();
        this.loadRegistrationSettings();
      }));
  }

  currentStyles() {
    return {
      'background': 'url('+environment.apiUrl+'/uploads/backgrounds/test.jpg) no-repeat fixed center',
      'background-size': 'cover',
    };
  }

  createForm() {
    this.registerForm = this.fb.group(
      {
        username: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(20)]],
        email: ['', [Validators.required, Validators.email]],
        password: this.fb.group(
          {
            first: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
            second: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
          },
          {validator: PasswordValidator.matchPassword}
        ),
        terms: ['', [Validators.requiredTrue]],
        accessCode: [null, [Validators.pattern(/^[A-Za-z0-9]{6}$/)]],
        channelCode: [appChannelCode()],
        localeCode: [this.translate.currentLang || this.translate.getDefaultLang() || 'pl-PL']
      }
    );
  }

  register(): void {
    this.ls.show();
    const accessCode = this.registerForm.get('accessCode')?.value;
    if (accessCode === '') {
      this.registerForm.get('accessCode')?.setValue(null);
    }

    this.regService.register(this.registerForm).subscribe(
      result => {
        this.rns.emitMessage(result.message);
        this.router.navigate(['/registration/check-email'], {
          state: {
            usernameOrEmail: this.registerForm.get('email')?.value
          }
        });
      },
      error => {
        this.reh.handleFormErrors(this.registerForm, error);
      }
    ).add(() => this.ls.hide());
  }

  private loadLegalRoutes(): void {
    this.staticPageRS.getNavigation('landing_footer').subscribe({
      next: (pages: StaticPageApiModel[]) => {
        const termsPage = pages.find((page) => page.documentType === 'TERMS');
        const privacyPage = pages.find((page) => page.documentType === 'PRIVACY_POLICY');

        this.termsRoute = termsPage ? this.staticPageRS.buildPublicRoute(termsPage) : '/pages/terms';
        this.privacyPolicyRoute = privacyPage ? this.staticPageRS.buildPublicRoute(privacyPage) : '/pages/privacy-policy';
      }
    });
  }

  private loadRegistrationSettings(): void {
    const localeCode = this.translate.currentLang || this.translate.getDefaultLang() || 'pl-PL';
    this.registerForm.get('localeCode')?.setValue(localeCode);

    this.appConfigRS.getCurrent(appChannelCode(), localeCode).subscribe({
      next: (config: AppConfigApiModel) => {
        this.appConfig = config;
        this.showAccessCode = !!config.standardRegistrationAccessCodeEnabled;

        const control = this.registerForm.get('accessCode');
        const validators = [Validators.pattern(/^[A-Za-z0-9]{6}$/)];

        if (config.standardRegistrationAccessCodeEnabled) {
          validators.unshift(Validators.required);
        }

        control?.setValidators(validators);
        control?.updateValueAndValidity();
      }
    });
  }
}
